import React, { useState, useEffect, useContext, useRef } from 'react';
import HotelSearchPanel from './HotelSearchPanel';
import './Hotel.css';
import { LandingContext } from '../Profile/LandingContext';
import DefaultData from '../../CompanyData/DefaultData';

const useInView = (options) => {
    const ref = useRef(null);
    const [inView, setInView] = useState(false);
    useEffect(() => {
        const observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) { setInView(true); observer.unobserve(entry.target); }
        }, options);
        if (ref.current) observer.observe(ref.current);
        return () => { if (ref.current) observer.unobserve(ref.current); };
    }, [ref, options]);
    return [ref, inView];
};

const HotelSearch = () => {
    const landingData = useContext(LandingContext);
    const [offersRef, offersInView]             = useInView({ threshold: 0.1 });
    const [destinationsRef, destinationsInView] = useInView({ threshold: 0.1 });
    const [whyUsRef, whyUsInView]               = useInView({ threshold: 0.1 });

    const hotelDeals = [
        { brand: 'Taj',      logoUrl: '/assets/images/hotellogos/taj.png',      title: 'City Break Specials',    description: 'Discover top-rated city hotels with world-class amenities.', discount: 'Up to 30% OFF',  type: 'percent' },
        { brand: 'Marriott', logoUrl: '/assets/images/hotellogos/marriott.png', title: 'Luxury Resort Stays',    description: 'Unwind at breathtaking resorts across the globe.',            discount: 'Flat ₹7,500 OFF', type: 'flat' },
        { brand: 'Hyatt',    logoUrl: '/assets/images/hotellogos/hyatt.png',    title: 'Weekend Getaway Deals',  description: 'Spontaneous stays at prices too good to ignore.',             discount: 'From ₹3,499',    type: 'price' },
    ];

    const popularDestinations = [
        { name: 'Goa, India',      image: 'https://images.unsplash.com/photo-1607577070449-531c2296916c?q=80&w=1025' },
        { name: 'Dubai, UAE',      image: 'https://images.unsplash.com/photo-1518684079-3c830dcef090?q=80&w=1974' },
        { name: 'Maldives',        image: 'https://images.unsplash.com/photo-1573843981267-be1999ff37cd?w=600' },
        { name: 'Bali, Indonesia', image: 'https://plus.unsplash.com/premium_photo-1682913629540-3857602b540c?w=600' },
    ];

    const whyChooseUs = [
        { icon: 'ri-price-tag-3-line',       title: 'Best Price Guarantee',  text: 'Competitive rates on 500,000+ hotels worldwide — guaranteed.' },
        { icon: 'ri-shield-check-line',       title: 'Secure Payments',       text: 'Bank-grade encryption keeps every transaction safe.' },
        { icon: 'ri-customer-service-2-line', title: '24/7 Support',          text: 'Our team is always ready to assist you with your stay.' },
    ];

    const heroStyle = {
        backgroundImage: landingData?.hotelBackgroundSrc
            ? `url(${landingData.hotelBackgroundSrc})`
            : 'url(/assets/images/hotelsearch.png)',
    };

    return (
        <div className="main-content">
            {/* ── Hero ─────────────────────────────────── */}
            <section className="hotel-hero-section" style={heroStyle}>
                <h1 className="hotel-hero-title">Search &amp; Book Hotels</h1>
                <p className="hotel-hero-subtitle">Find the perfect stay for every destination</p>
                <div className="hotel-search-container">
                    <HotelSearchPanel />
                </div>
            </section>

            {/* ── Body ─────────────────────────────────── */}
            <div className="page-content" style={{ paddingTop: 0 }}>
                <div className="container py-5">

                    {/* Offers */}
                    <section ref={offersRef} className={`py-5 section-container${offersInView ? ' is-visible' : ''}`}>
                        <h2 className="section-title">Exclusive Hotel Offers</h2>
                        <div className="row g-4">
                            {hotelDeals.map((deal, i) => (
                                <div className="col-lg-4 col-md-6" key={i}>
                                    <div className={`hotel-offer-card offer-card-${deal.type}`}>
                                        <div className="offer-card-header">
                                            <img src={deal.logoUrl} alt={deal.brand} className="airline-logo" />
                                            <span className="discount-badge">{deal.discount}</span>
                                        </div>
                                        <div className="offer-card-body">
                                            <h5 className="card-title">{deal.title}</h5>
                                            <p className="card-text">{deal.description}</p>
                                            <a href="#!" className="stretched-link">
                                                Book Now <i className="ri-arrow-right-line" />
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Popular stays */}
                    <section ref={destinationsRef} className={`py-5 section-container${destinationsInView ? ' is-visible' : ''}`}>
                        <h2 className="section-title">Popular Stay Destinations</h2>
                        <div className="row g-4">
                            {popularDestinations.map((dest, i) => (
                                <div className="col-lg-3 col-md-6" key={i}>
                                    <a href="#!" className="destination-card-modern">
                                        <img src={dest.image} alt={dest.name} className="card-img" />
                                        <div className="card-img-overlay">
                                            <h4 className="card-title">{dest.name}</h4>
                                        </div>
                                    </a>
                                </div>
                            ))}
                        </div>
                    </section>

                    {/* Why us */}
                    <section ref={whyUsRef} className={`py-5 section-container why-us-section${whyUsInView ? ' is-visible' : ''}`}>
                        <h2 className="section-title">Why Book With Us?</h2>
                        <div className="row g-4">
                            {whyChooseUs.map((item, i) => (
                                <div className="col-lg-4 col-md-6" key={i}>
                                    <div className="why-us-item">
                                        <div className="why-us-icon"><i className={item.icon} /></div>
                                        <div className="why-us-content">
                                            <h5 className="title">{item.title}</h5>
                                            <p className="text">{item.text}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </section>

                </div>
            </div>
        </div>
    );
};

export default HotelSearch;
